/**
 * SongTable Component
 * Displays the song stats table for a station with sortable columns
 * v2.0: Added Early Drop and LSI indicators
 * v2.1: Added RankingBadgeWithTooltip with colored explanation panels
 * v2.2: Added Song Details Modal on artist/title click
 */

import React, { useState, useMemo, useCallback, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { RefreshCw, AlertCircle, ChevronUp, ChevronDown, ChevronsUpDown, Info, X, Clock, Search } from 'lucide-react';
import { getScoreColor, getRankingCategoryStyle, getRankingCategoryLabel } from './utils';
import { getBackendUrl } from '../../utils/api';
import AudienceImpactBadge from '../AudienceImpactBadge';
import PreviewPlayButton from '../shared/PreviewPlayButton';

// Helper function to decode HTML entities (apostrophes, quotes, etc.)
const decodeHtmlEntities = (text) => {
  if (!text || typeof text !== 'string') return text;
  
  // Create a textarea element to decode HTML entities
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  let decoded = textarea.value;
  
  // Also handle common entities manually for cases the browser misses
  decoded = decoded
    .replace(/&apos;/g, "'")
    .replace(/&#39;/g, "'")
    .replace(/&#x27;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&#34;/g, '"')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&#x2019;/g, "'")  // Right single quotation mark
    .replace(/&#8217;/g, "'")   // Right single quotation mark (decimal)
    .replace(/'/g, "'")         // Unicode right single quote
    .replace(/'/g, "'")         // Unicode left single quote
    .replace(/"/g, '"')         // Unicode left double quote
    .replace(/"/g, '"');        // Unicode right double quote
  
  return decoded;
};

// Song Details Modal Component - with play times and daypart info
/**
 * Compact, Draggable Song Details Modal
 * v2.6: Uses table data as source of truth for stats
 * v2.7: Added daypart labels for each play time
 * - Stats (plays, retention, etc.) come from the song object (table data)
 * - Only play TIMES are fetched from API
 * - More compact layout (no scroll for day/yesterday periods)
 * - Draggable for viewing table behind
 * - Multiple instances for side-by-side comparison
 */

// Daypart helper function
const getDaypartForTime = (timeStr) => {
  // Parse time from various formats:
  // - "HH:MM" (simple time)
  // - "DD/MM HH:MM" (date + time from unique_play_hours)
  
  let hour = 0;
  
  if (timeStr && typeof timeStr === 'string') {
    // Check if format includes date (DD/MM HH:MM)
    if (timeStr.includes(' ')) {
      // Extract time part after the space
      const timePart = timeStr.split(' ')[1]; // "HH:MM"
      hour = parseInt(timePart?.split(':')[0], 10) || 0;
    } else if (timeStr.includes(':')) {
      // Simple "HH:MM" format
      hour = parseInt(timeStr.split(':')[0], 10) || 0;
    }
  }
  
  if (hour >= 6 && hour < 11) return { key: 'morning', label: 'Morning', color: 'text-yellow-500' };
  if (hour >= 11 && hour < 15) return { key: 'midday', label: 'Midday', color: 'text-orange-400' };
  if (hour >= 15 && hour < 18) return { key: 'afternoon', label: 'Afternoon', color: 'text-amber-500' };
  if (hour >= 18 && hour < 22) return { key: 'drive', label: 'Drive', color: 'text-purple-400' };
  return { key: 'night', label: 'Night', color: 'text-blue-400' };
};

// Calculate daypart distribution summary
const getDaypartSummary = (playTimes) => {
  if (!playTimes || playTimes.length === 0) return null;
  
  const counts = { morning: 0, midday: 0, afternoon: 0, drive: 0, night: 0 };
  playTimes.forEach(pt => {
    const dp = getDaypartForTime(pt.time);
    counts[dp.key]++;
  });
  
  // Build summary string with only non-zero dayparts
  const parts = [];
  if (counts.morning > 0) parts.push(`Morning ${counts.morning}`);
  if (counts.midday > 0) parts.push(`Midday ${counts.midday}`);
  if (counts.afternoon > 0) parts.push(`Afternoon ${counts.afternoon}`);
  if (counts.drive > 0) parts.push(`Drive ${counts.drive}`);
  if (counts.night > 0) parts.push(`Night ${counts.night}`);
  
  return parts.length > 0 ? parts.join(' · ') : null;
};

const SongDetailsModal = ({ 
  song, 
  onClose, 
  stationId, 
  period, 
  isAdmin = true, 
  userId = null,
  instanceId = 0,  // 0 or 1 for dual-modal support
  totalInstances = 1
}) => {
  const [playTimes, setPlayTimes] = useState(null);
  const [loadingPlayTimes, setLoadingPlayTimes] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const [position, setPosition] = useState({ 
    x: instanceId === 0 ? 50 : window.innerWidth - 420, 
    y: 80 
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const modalRef = useRef(null);
  
  // Track mobile state
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Handle dragging (disabled on mobile)
  const handleMouseDown = (e) => {
    if (isMobile) return; // Disable drag on mobile
    if (e.target.closest('button') || e.target.closest('a')) return;
    setIsDragging(true);
    const rect = modalRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };
  
  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    const newX = Math.max(0, Math.min(window.innerWidth - 380, e.clientX - dragOffset.x));
    const newY = Math.max(0, Math.min(window.innerHeight - 100, e.clientY - dragOffset.y));
    setPosition({ x: newX, y: newY });
  }, [isDragging, dragOffset]);
  
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);
  
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);
  
  // v2.12: Use unique_play_hours from song object (no API call needed)
  // The stats API now returns the play hours directly, making popup a true mirror of table
  useEffect(() => {
    if (song) {
      // Build play times data from song object
      const uniqueHours = song.unique_play_hours || [];
      
      if (uniqueHours.length > 0) {
        // Create play_times structure from unique_play_hours
        const playTimesData = {
          total_plays: song.plays,
          play_times: uniqueHours.map(hour => ({
            time: hour,
            // We don't have detailed per-play stats, just the time
            from_table: true
          }))
        };
        setPlayTimes(playTimesData);
      } else {
        // Fallback: No unique hours available, show empty
        setPlayTimes({
          total_plays: song.plays,
          play_times: []
        });
      }
      setLoadingPlayTimes(false);
    }
  }, [song]);
  
  if (!song) return null;
  
  // Compact metric helpers
  const getRetentionColor = (rate) => {
    if (rate >= 90) return 'text-green-600';
    if (rate >= 75) return 'text-blue-600';
    if (rate >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };
  
  const getLSIBadge = (lsi) => {
    if (lsi === null || lsi === undefined) return '—';
    if (lsi >= 1.0) return '💎';
    if (lsi >= 0.9) return '✨';
    if (lsi >= 0.8) return '•';
    return '⚡';
  };
  
  // Determine if this is a multi-day period (needs scroll for play times)
  const isMultiDayPeriod = period && !['today', 'yesterday'].includes(period);
  const playCount = playTimes?.play_times?.length || 0;
  const needsScroll = isMultiDayPeriod && playCount > 5;
  
  return (
    <>
      {/* Mobile Overlay */}
      {isMobile && (
        <div 
          className="fixed inset-0 bg-black/50 z-40"
          onClick={onClose}
        />
      )}
      <div 
        ref={modalRef}
        className={`fixed z-50 bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 ${
          isMobile 
            ? 'inset-x-2 top-1/2 -translate-y-1/2 max-h-[85vh] overflow-y-auto' 
            : ''
        }`}
        style={isMobile ? {} : { 
          left: position.x, 
          top: position.y, 
          width: '360px',
          cursor: isDragging ? 'grabbing' : 'default'
        }}
        data-testid={`song-modal-${instanceId}`}
      >
        {/* Draggable Header */}
        <div 
          className={`flex items-center justify-between px-3 py-2 bg-gradient-to-r from-purple-600 to-blue-600 rounded-t-lg select-none ${
            isMobile ? 'sticky top-0 z-10' : 'cursor-grab'
          }`}
          onMouseDown={handleMouseDown}
        >
        {/* v2.9.78 — show Shazam cover art if we have it. Stays small
            (40x40) so the modal layout doesn't shift. Falls back to
            a music-note placeholder when there's no Shazam art.
            v2.9.82 — wrapped with PreviewPlayButton so the popup
            header doubles as a 30s preview play control.
            v2.9.87 — ALWAYS render the slot (even when cover_art_url
            and preview_url are both null). Previously we hid the
            entire div which made the popup look broken while the
            backfill worker was still catching up. Now the
            PreviewPlayButton component itself decides what to draw
            (cover thumbnail / music-note placeholder / play overlay)
            so the slot is always visually present. */}
        <div className="mr-2 shrink-0">
          <PreviewPlayButton
            src={song.cover_art_url}
            previewUrl={song.preview_url}
            trackKey={`popup-${song.normalized_key || song.title || ''}`}
            artist={song.artist}
            title={song.title}
            size="md"
            alwaysOn={false}
            testId="song-popup-preview-button"
          />
        </div>
        <div className="flex-1 min-w-0 mr-2">
          <h3 className="font-bold text-sm text-white truncate">{decodeHtmlEntities(song.artist)}</h3>
          <p className="text-xs text-white/80 truncate">{decodeHtmlEntities(song.title)}</p>
          {/* v2.9.78 — ISRC line (only if Shazam ταυτοποίησε το τραγούδι). */}
          {song.isrc && (
            <p
              className="text-[10px] text-white/60 truncate font-mono"
              title="International Standard Recording Code (από Shazam/Apple Music)"
              data-testid="song-isrc"
            >
              ISRC: {song.isrc}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1">
          {totalInstances > 1 && (
            <span className="text-[10px] text-white/60 mr-1">#{instanceId + 1}</span>
          )}
          <button 
            onClick={onClose}
            className="p-1 hover:bg-white/20 rounded text-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      {/* Compact Content */}
      <div className="p-3">
        {/* Stats Grid - Compact 4-column */}
        {/* v2.6: ALL stats come from song object (table data), NOT from API */}
        <div className="grid grid-cols-4 gap-2 mb-3">
          <div className="text-center" title="Συνολικές αναπαραγωγές σε όλους τους σταθμούς">
            <p className="text-[10px] text-gray-500 dark:text-gray-400">Plays</p>
            <p className="text-lg font-bold text-gray-900 dark:text-white">
              {song.plays || 0}
            </p>
          </div>
          <div className="text-center" title="Ποσοστό ακροατών που παρέμειναν μέχρι τέλος">
            <p className="text-[10px] text-gray-500 dark:text-gray-400">Ret%</p>
            <p className={`text-lg font-bold ${getRetentionColor(song.retention_rate)}`}>
              {song.retention_rate != null && song.retention_rate > 0 
                ? <>{!song.curve_retention && <span className="text-gray-400">~</span>}{Math.min(song.retention_rate, 100).toFixed(0)}%</>
                : '—'}
            </p>
          </div>
          <div className="text-center" title="Ποσοστό ακροατών που έφυγαν στα πρώτα 60 δευτερόλεπτα">
            <p className="text-[10px] text-gray-500 dark:text-gray-400">Early</p>
            <p className="text-lg font-bold text-gray-700 dark:text-gray-300">
              {song.early_drop_rate !== null && song.early_drop_rate !== undefined 
                ? `${song.early_drop_rate.toFixed(0)}%` 
                : '—'}
            </p>
          </div>
          <div className="text-center" title="Σταθερότητα ακροατηρίου κατά την αναπαραγωγή (1.00 = τέλεια σταθερό)">
            <p className="text-[10px] text-gray-500 dark:text-gray-400">LSI</p>
            <p className="text-lg font-bold text-gray-700 dark:text-gray-300">
              {song.lsi != null ? <>{getLSIBadge(song.lsi)} {song.lsi.toFixed(2)}</> : '—'}
            </p>
          </div>
        </div>
        
        {/* Secondary Stats Row - ALL from song object */}
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-3 pb-2 border-b border-gray-200 dark:border-gray-700">
          <span title="Σύνολο ωρών ακρόασης (ακροατές × διάρκεια ανά play, αθροισμένο)">TLH: <b className="text-purple-600">{song.total_tlh ?? song.total_proxy_tlh ?? 0}</b></span>
          <span title={`Σκορ = log(plays) × 55% + retention × 30% + (100 - early_drop) × 15%`}>Score: <b className="text-blue-600">{song.composite_score ?? song.adjusted_score ?? 0}</b></span>
          <span>Έτος: <b>{song.release_year || '—'}</b></span>
          {song.ranking_category && (
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
              song.ranking_category === 'POWER' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
              song.ranking_category === 'SECONDARY' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
              song.ranking_category === 'CURRENTS' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
              song.ranking_category === 'GOLD' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
              'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
            }`}>
              {song.ranking_category}
            </span>
          )}
        </div>
        
        {/* Play Times - Compact List with Daypart Labels */}
        {/* v2.7: Added daypart distribution summary and labels */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Ώρες Μετάδοσης
            </h4>
            {/* Show plays from table data */}
            <span className="text-[10px] text-gray-400">{song.plays || 0} plays</span>
          </div>
          
          {/* Daypart Distribution Summary */}
          {playTimes && playTimes.play_times?.length > 0 && (
            <div className="mb-2 px-2 py-1.5 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/30 dark:to-blue-900/30 rounded text-[10px] text-gray-600 dark:text-gray-300">
              <span className="text-gray-500 dark:text-gray-400">Κατανομή:</span>{' '}
              <span className="font-medium">{getDaypartSummary(playTimes.play_times) || '—'}</span>
            </div>
          )}
          
          {loadingPlayTimes ? (
            <div className="text-center py-2 text-gray-500 text-xs">
              <RefreshCw className="w-3 h-3 animate-spin inline mr-1" />
              Φόρτωση ωρών...
            </div>
          ) : playTimes && playTimes.play_times?.length > 0 ? (
            <div className={`space-y-1 ${needsScroll ? 'max-h-[150px] overflow-y-auto pr-1' : ''}`}>
              {playTimes.play_times.map((pt, idx) => {
                const daypart = getDaypartForTime(pt.time);
                return (
                  <div 
                    key={idx}
                    className="flex items-center justify-between px-2 py-1 rounded bg-purple-50 dark:bg-purple-900/20 text-xs"
                  >
                    <span className="font-mono text-purple-700 dark:text-purple-300">
                      {pt.time}
                      {playTimes.play_times.length > 1 && pt.date && (
                        <span className="ml-1 text-purple-500/70">({pt.date})</span>
                      )}
                    </span>
                    <div className="flex items-center gap-1">
                      <span className={`text-[9px] ${daypart.color}`}>
                        {daypart.label}
                      </span>
                      {isAdmin && pt.fingerprint_used && (
                        <span className="text-[9px] bg-orange-100 dark:bg-orange-900/40 text-orange-600 dark:text-orange-400 px-1 rounded">
                          🎤 FP
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-xs text-gray-400 text-center py-2">
              Δεν υπάρχουν ώρες μετάδοσης
            </p>
          )}
        </div>
        
        {/* Footer hint - only on desktop */}
        {!isMobile && (
          <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700/50 text-[10px] text-gray-400 text-center">
            Σύρε το header για μετακίνηση
          </div>
        )}
      </div>
    </div>
    </>
  );
};

// Ranking category explanations with colors matching the badge styles
const RANKING_EXPLANATIONS = {
  'POWER': {
    title: '🔥 POWER',
    description: 'Κορυφαία τρέχουσα επιτυχία',
    details: 'Score ≥85% | Τραγούδια ≤2 ετών | Max 8% του playlist',
    tip: 'Συνέχισε να τα παίζεις σε prime ώρες',
    color: '#ef4444' // red-500
  },
  'SECONDARY': {
    title: '⭐ SECONDARY',
    description: 'Πολύ δυνατό τρέχον τραγούδι',
    details: 'Score ≥70% | Τραγούδια ≤2 ετών | Max 15% του playlist',
    tip: 'Καλή απόδοση, διατήρησε τη συχνότητα',
    color: '#f97316' // orange-500
  },
  'CURRENTS': {
    title: '✅ CURRENTS',
    description: 'Τρέχον τραγούδι σε rotation',
    details: 'Score ≥55% | Τραγούδια ≤2 ετών',
    tip: 'Σταθερή επιλογή για το format σου',
    color: '#22c55e' // green-500
  },
  'RECURRENTS': {
    title: '🔄 RECURRENTS',
    description: 'Παλαιότερο τραγούδι με καλή απήχηση',
    details: 'Score ≥55% | Τραγούδια 3-5 ετών',
    tip: 'Κρατάει ακόμα - ιδανικό για mix με νέα',
    color: '#6366f1' // indigo-500
  },
  'GOLD': {
    title: '📚 GOLD',
    description: 'Διαχρονική επιτυχία',
    details: 'Score ≥55% | Τραγούδια 6+ ετών',
    tip: 'Classic που αρέσει ακόμα στο κοινό σου',
    color: '#f59e0b' // amber-500
  },
  'SLOW DOWN': {
    title: '⚠️ SLOW DOWN',
    description: 'Χρειάζεται μείωση συχνότητας',
    details: 'Retention 40-59% | Αρχίζει να κουράζει',
    tip: 'Μείωσε τα plays ή άλλαξε ώρες μετάδοσης',
    color: '#eab308' // yellow-500
  },
  'CUT': {
    title: '❌ CUT',
    description: 'Χαμηλή απόδοση',
    details: 'Retention <40% | Οι ακροατές φεύγουν',
    tip: 'Σκέψου να το αφαιρέσεις ή να το παίζεις σπάνια',
    color: '#9ca3af' // gray-400
  },
  'NEW': {
    title: '—',
    description: 'Πρόσφατη ταξινόμηση',
    details: 'Αξιολόγηση σε εξέλιξη',
    tip: '',
    color: '#9ca3af' // gray-400 (neutralised)
  },
  'ADDED': {
    title: '—',
    description: 'Πρόσφατη ταξινόμηση',
    details: 'Αξιολόγηση σε εξέλιξη',
    tip: '',
    color: '#9ca3af' // gray-400 (neutralised)
  },
  'PENDING': {
    title: '⏳ PENDING',
    description: 'Αναμένει αξιολόγηση',
    details: 'Δεν έχει αρκετά δεδομένα ακόμα',
    tip: 'Θα αξιολογηθεί μόλις συγκεντρωθούν περισσότερα plays',
    color: '#a855f7' // purple-500
  }
};

// Enhanced Ranking Badge with colored tooltip panel - FIXED positioning and styling
const RankingBadgeWithTooltip = ({ category, releaseYearUnknown, isNewEntry, daysInRotation, releaseYear, isAddedEntry }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0, ready: false });
  const badgeRef = useRef(null);
  const tooltipRef = useRef(null);
  
  const explanation = RANKING_EXPLANATIONS[category] || {
    title: '❓ Άγνωστο',
    description: 'Δεν υπάρχουν επαρκή δεδομένα',
    details: 'Περιμένουμε περισσότερες μεταδόσεις',
    tip: '',
    color: '#6b7280' // gray-500
  };
  
  // Build extra info for NEW/ADDED entries
  let extraInfo = '';
  if (isNewEntry) {
    extraInfo = `${daysInRotation} ημέρες στο rotation`;
    if (releaseYear) extraInfo += ` • Release: ${releaseYear}`;
  } else if (isAddedEntry) {
    extraInfo = 'Παλιό τραγούδι προστέθηκε στο rotation';
  }

  // After mount, measure the REAL tooltip height and reposition so multi-line
  // panels never overflow the viewport top edge. The first paint is hidden
  // (visibility: hidden) until this measurement runs — no flash, no jitter.
  useLayoutEffect(() => {
    if (!showTooltip || !badgeRef.current || !tooltipRef.current) return;
    const rect = badgeRef.current.getBoundingClientRect();
    const tooltipWidth = 280;
    const tooltipHeight = tooltipRef.current.offsetHeight || 200;
    const cursorOffset = 18;
    const safeMargin = 12;

    const spaceAbove = rect.top;
    const spaceBelow = window.innerHeight - rect.bottom;

    let y;
    if (spaceAbove >= tooltipHeight + cursorOffset + safeMargin) {
      y = rect.top - tooltipHeight - cursorOffset;
    } else if (spaceBelow >= tooltipHeight + cursorOffset + safeMargin) {
      y = rect.bottom + cursorOffset;
    } else {
      y = Math.max(safeMargin, window.innerHeight - tooltipHeight - safeMargin);
    }

    const badgeCenter = rect.left + rect.width / 2;
    let x = badgeCenter - tooltipWidth / 2;
    x = Math.max(safeMargin, Math.min(x, window.innerWidth - tooltipWidth - safeMargin));

    setTooltipPos({ x, y, ready: true });
  }, [showTooltip]);

  const handleMouseEnter = () => {
    // Compute an immediate fallback position BEFORE useLayoutEffect runs,
    // so the tooltip becomes visible on the very first frame even if the
    // ref hasn't been attached yet (e.g. React strict-mode double-render).
    // useLayoutEffect refines this position using the real measured height.
    if (badgeRef.current) {
      const rect = badgeRef.current.getBoundingClientRect();
      const tooltipWidth = 280;
      const safeMargin = 12;
      const badgeCenter = rect.left + rect.width / 2;
      let x = badgeCenter - tooltipWidth / 2;
      x = Math.max(safeMargin, Math.min(x, window.innerWidth - tooltipWidth - safeMargin));
      // Default: above badge (assume 200 px until refined)
      let y = rect.top - 200 - 18;
      if (y < safeMargin) y = rect.bottom + 18;
      setTooltipPos({ x, y, ready: true });
    }
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
    setTooltipPos((p) => ({ ...p, ready: false }));
  };
  
  return (
    <div 
      className="relative inline-block"
      ref={badgeRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Badge */}
      <span 
        className={`cursor-help ${getRankingCategoryStyle(category)}`}
        onClick={() => {
          if (!showTooltip) handleMouseEnter();
          else handleMouseLeave();
        }}
      >
        {getRankingCategoryLabel(category, releaseYearUnknown)}
      </span>
      
      {/* Tooltip Panel - rendered via portal to escape any ancestor
          with overflow/transform/z-index that could clip or hide it. */}
      {showTooltip && createPortal(
        <div 
          ref={tooltipRef}
          data-testid="ranking-tooltip"
          style={{
            position: 'fixed',
            zIndex: 999999,
            width: '280px',
            left: `${tooltipPos.x}px`,
            top: `${tooltipPos.y}px`,
            visibility: tooltipPos.ready ? 'visible' : 'hidden',
            backgroundColor: '#0a0a0a',
            borderRadius: '10px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,255,255,0.1)',
            borderLeft: `5px solid ${explanation.color}`,
            padding: '14px 16px',
            pointerEvents: 'none',
          }}
        >
          {/* Title */}
          <div style={{ 
            fontWeight: 'bold', 
            fontSize: '15px', 
            color: explanation.color,
            marginBottom: '8px',
            textShadow: `0 0 10px ${explanation.color}40`
          }}>
            {explanation.title}
          </div>
          
          {/* Description */}
          <div style={{ 
            fontSize: '13px', 
            color: '#f3f4f6',
            marginBottom: '10px',
            lineHeight: '1.4'
          }}>
            {explanation.description}
          </div>
          
          {/* Details */}
          <div style={{ 
            fontSize: '11px', 
            color: '#d1d5db',
            backgroundColor: 'rgba(255,255,255,0.08)',
            padding: '8px 10px',
            borderRadius: '6px',
            fontFamily: 'ui-monospace, monospace',
            border: '1px solid rgba(255,255,255,0.1)'
          }}>
            {explanation.details}
          </div>
          
          {/* Extra Info for NEW/ADDED */}
          {extraInfo && (
            <div style={{ 
              fontSize: '11px', 
              color: '#60a5fa',
              marginTop: '8px',
              fontStyle: 'italic'
            }}>
              📍 {extraInfo}
            </div>
          )}
          
          {/* Tip */}
          {explanation.tip && (
            <div style={{ 
              fontSize: '11px', 
              marginTop: '10px',
              paddingTop: '10px',
              borderTop: '1px solid rgba(255,255,255,0.15)'
            }}>
              <span style={{ color: '#9ca3af' }}>💡 </span>
              <span style={{ color: explanation.color, fontWeight: '500' }}>{explanation.tip}</span>
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

// Ranking category order for sorting (outside component to avoid dependency warning)
const RANK_ORDER = {
  "POWER": 1, 
  "SECONDARY": 2, 
  "CURRENTS": 3, 
  "RECURRENTS": 4, 
  "GOLD": 5, 
  "NEW": 6, 
  "ADDED": 7, 
  "SLOW DOWN": 8, 
  "CUT": 9,
  "PENDING": 10,
  null: 99,
  undefined: 99
};

// Sort indicator component (outside main component)
const SortIndicator = ({ sortKey, currentKey, direction }) => {
  if (sortKey !== currentKey) {
    return <ChevronsUpDown className="w-3 h-3 opacity-30" />;
  }
  return direction === 'desc' 
    ? <ChevronDown className="w-3 h-3 text-blue-500" />
    : <ChevronUp className="w-3 h-3 text-blue-500" />;
};

// Early Drop Indicator - Visual bar showing tune-out in first 30 seconds (intro)
const EarlyDropIndicator = ({ rate, retentionRate, hasAdvancedMetrics, metricsReliability }) => {
  // Priority 1: If rate is null/undefined, show "—" (no data at all)
  // This covers stations without listener sources AND songs too short to calculate
  if (rate === null || rate === undefined) {
    if (!hasAdvancedMetrics) {
      return (
        <span 
          className="text-gray-400 text-xs cursor-help" 
          title="Δεν υπάρχουν δεδομένα ακροατών για τον υπολογισμό Early Drop"
        >
          —
        </span>
      );
    }
    return <span className="text-gray-400">—</span>;
  }
  
  // Priority 2: Has data but reliability is "none" (< 10 listeners) - show grayed indicator
  if (metricsReliability === 'none') {
    return (
      <div 
        className="flex items-center gap-1 opacity-40 cursor-help" 
        title="Λιγότεροι από 10 ακροατές - χαμηλή αξιοπιστία"
      >
        <div className="flex gap-0.5">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="w-1 h-3 bg-gray-400 rounded-sm" />
          ))}
        </div>
        <span className="text-xs text-gray-400">~{rate.toFixed(0)}%</span>
      </div>
    );
  }
  
  // Check if this is low reliability (11-30 listeners)
  const isLowReliability = metricsReliability === 'low';
  
  // If we have early drop data, use it
  // But also consider overall retention for color coding
  const effectiveRate = rate;
  
  // Determine color based on BOTH early drop AND overall retention
  // If retention is very low (<50%), show warning even if early drop is 0%
  let color, filledBars, introLabel, overallLabel;
  
  // Get overall retention (capped at 100%)
  const overallRetention = retentionRate !== null && retentionRate !== undefined 
    ? Math.min(retentionRate, 100) 
    : null;
  
  // Check if retention is concerningly low (below 50%)
  const hasLowRetention = overallRetention !== null && overallRetention < 50;
  const hasMediumRetention = overallRetention !== null && overallRetention >= 50 && overallRetention < 80;
  const hasGoodRetention = overallRetention !== null && overallRetention >= 80;
  
  // Determine intro performance (Early Drop in first 30 seconds)
  if (effectiveRate <= 2) {
    introLabel = '✅ Εξαιρετικό intro - Σχεδόν κανείς δεν έφυγε';
  } else if (effectiveRate <= 5) {
    introLabel = '✅ Καλό intro';
  } else if (effectiveRate <= 10) {
    introLabel = '⚠️ Μέτριο intro';
  } else {
    introLabel = '❌ Κακό intro - Πολλοί έφυγαν νωρίς';
  }
  
  // Determine overall retention label
  if (overallRetention === null) {
    overallLabel = '📊 Συνολικό Retention: Μη διαθέσιμο';
  } else if (hasGoodRetention) {
    overallLabel = `🎉 Συνολικό Retention: ${overallRetention.toFixed(0)}%\n   Οι περισσότεροι έμειναν μέχρι το τέλος!`;
  } else if (hasMediumRetention) {
    overallLabel = `📊 Συνολικό Retention: ${overallRetention.toFixed(0)}%\n   Περίπου οι μισοί έμειναν μέχρι το τέλος`;
  } else {
    overallLabel = `📉 Συνολικό Retention: ${overallRetention.toFixed(0)}%\n   Λίγοι έμειναν μέχρι το τέλος`;
  }
  
  // Determine bar color based on overall performance
  if (effectiveRate <= 2 && hasGoodRetention) {
    color = 'bg-green-500';
    filledBars = 4;
  } else if (effectiveRate <= 5 && !hasLowRetention) {
    color = 'bg-green-500';
    filledBars = 3;
  } else if (effectiveRate <= 10 || hasMediumRetention) {
    color = 'bg-yellow-500';
    filledBars = 2;
  } else {
    color = 'bg-red-500';
    filledBars = 1;
  }
  
  // Special case: good intro but low overall retention = listeners leave AFTER intro
  const specialWarning = (effectiveRate <= 5 && hasLowRetention) 
    ? '\n\n⚠️ ΠΡΟΣΟΧΗ: Οι ακροατές φεύγουν ΜΕΤΑ το intro!\n   Πιθανό πρόβλημα με το κυρίως τραγούδι.' 
    : '';
  
  // Calculate how many stayed in intro
  const stayedIntro = (100 - effectiveRate).toFixed(0);
  
  // Build tooltip
  const tooltip = [
    `━━━ INTRO (πρώτα 30") ━━━`,
    `Early Drop: ${effectiveRate.toFixed(1)}%`,
    `📉 ${effectiveRate.toFixed(0)}% έφυγαν στα πρώτα 30"`,
    `📊 ${stayedIntro}% έμειναν μετά το intro`,
    `${introLabel}`,
    ``,
    `━━━ ΣΥΝΟΛΙΚΟ ΤΡΑΓΟΥΔΙ ━━━`,
    `${overallLabel}`,
    `${specialWarning}`,
    ``,
    `(Περισσότερες μπάρες = καλύτερη απόδοση)`,
    isLowReliability ? `\n⚠️ Ένδειξη (~): Χαμηλός αριθμός ακροατών, εκτίμηση` : ''
  ].filter(line => line !== undefined && line !== '').join('\n');
  
  // For low reliability, show with ~ prefix and grayed colors
  const displayRate = isLowReliability ? `~${effectiveRate.toFixed(0)}%` : `${effectiveRate.toFixed(0)}%`;
  const textColorClass = isLowReliability ? 'text-gray-400' : 'text-gray-500';
  const barOpacity = isLowReliability ? 'opacity-50' : '';
  
  return (
    <div 
      className={`flex items-center gap-1 ${isLowReliability ? 'opacity-70' : ''}`}
      title={tooltip}
    >
      <div className={`flex gap-0.5 ${barOpacity}`}>
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className={`w-1.5 h-3 rounded-sm ${i <= filledBars ? color : 'bg-gray-300 dark:bg-gray-600'}`}
          />
        ))}
      </div>
      <span className={`text-[10px] ${textColorClass} ml-0.5`}>{displayRate}</span>
    </div>
  );
};

// LSI Badge - Listener Stability Index
const LSIBadge = ({ lsi, hasAdvancedMetrics, metricsReliability }) => {
  // Priority 1: If LSI is null/undefined, show "—" (no data at all)
  if (lsi === null || lsi === undefined) {
    if (!hasAdvancedMetrics) {
      return (
        <span 
          className="text-gray-400 text-xs cursor-help" 
          title="Δεν υπάρχουν δεδομένα ακροατών για τον υπολογισμό LSI"
        >
          —
        </span>
      );
    }
    return <span className="text-gray-400">—</span>;
  }
  
  // Priority 2: Has LSI data but reliability is "none" (< 10 listeners)
  if (metricsReliability === 'none') {
    return (
      <div 
        className="opacity-40 cursor-help" 
        title={`LSI: ${lsi.toFixed(2)} - Λιγότεροι από 10 ακροατές, χαμηλή αξιοπιστία`}
      >
        <span className="text-gray-400 text-xs">~{lsi.toFixed(2)}</span>
      </div>
    );
  }
  
  // Check if this is low reliability (11-30 listeners)
  const isLowReliability = metricsReliability === 'low';
  
  let badge, color, label, extraInfo;
  if (lsi >= 1.0) {
    badge = isLowReliability ? '~💎' : '💎';
    color = isLowReliability ? 'text-gray-400' : 'text-blue-500';
    label = 'Πολύ σταθερό κοινό';
    extraInfo = hasAdvancedMetrics ? '' : '\n⚠️ Βασισμένο σε λίγα δείγματα';
  } else if (lsi >= 0.9) {
    badge = isLowReliability ? '~✨' : '✨';
    color = isLowReliability ? 'text-gray-400' : 'text-green-500';
    label = 'Σταθερό κοινό';
    extraInfo = '';
  } else if (lsi >= 0.8) {
    badge = isLowReliability ? '~•' : '•';
    color = isLowReliability ? 'text-gray-400' : 'text-yellow-500';
    label = 'Μέτριο';
    extraInfo = '';
  } else {
    badge = isLowReliability ? '~⚡' : '⚡';
    color = isLowReliability ? 'text-gray-400' : 'text-orange-400';
    label = 'Ασταθές κοινό - πολύ tune-in/tune-out';
    extraInfo = '';
  }
  
  const reliabilityNote = isLowReliability ? '\n\n⚠️ Ένδειξη (~): Χαμηλός αριθμός ακροατών, εκτίμηση' : '';
  
  return (
    <span 
      className={`${color} cursor-help ${isLowReliability ? 'opacity-70' : ''}`}
      title={`LSI: ${lsi.toFixed(2)} - ${label}\nΔείκτης σταθερότητας ακροατών${extraInfo}${reliabilityNote}`}
    >
      {badge}
    </span>
  );
};

// Curve Retention Mini Chart
const CurveRetentionBadge = ({ curveRetention, legacyRetention }) => {
  if (curveRetention === null || curveRetention === undefined) {
    return null;
  }
  
  // Show difference between curve and legacy (if significant)
  const diff = curveRetention - (legacyRetention || curveRetention);
  const showDiff = Math.abs(diff) >= 2;
  
  return (
    <span 
      className="text-[9px] text-purple-500 ml-1 cursor-help"
      title={`Curve Retention: ${curveRetention.toFixed(1)}%\nΜετράει τη συνολική εμπειρία ακροατών σε όλη τη διάρκεια`}
    >
      {showDiff && (
        <span className={diff > 0 ? 'text-green-500' : 'text-red-500'}>
          {diff > 0 ? '▲' : '▼'}
        </span>
      )}
    </span>
  );
};

// Chart Confidence Badge - Shows data reliability
const ConfidenceBadge = ({ confidence }) => {
  if (!confidence) return null;
  
  const badges = {
    high: { icon: '🔵', color: 'text-blue-400', label: 'Υψηλή αξιοπιστία (fingerprint)' },
    medium: { icon: '🟢', color: 'text-green-400', label: 'Μέτρια αξιοπιστία (metadata + duration)' },
    low: { icon: '🟡', color: 'text-yellow-400', label: 'Χαμηλή αξιοπιστία (metadata only)' }
  };
  
  const badge = badges[confidence];
  if (!badge) return null;
  
  return (
    <span 
      className={`${badge.color} cursor-help ml-1 text-xs`}
      title={`Chart Confidence: ${confidence.toUpperCase()}\n${badge.label}`}
    >
      {badge.icon}
    </span>
  );
};

const SongTable = ({ 
  stats, 
  loading = false,
  sortConfig: externalSortConfig,
  onSortChange,
  listenerThresholds,
  stationId,
  period,
  isAdmin = true,  // Default to admin for backward compatibility
  userId = null,   // User ID for non-admin access
  isDaypartView = false,  // Dayparts = TIME FILTER ONLY, doesn't change metrics
  impactLookup: externalImpact = null  // Audience Impact lookup (optional external)
}) => {
  const [internalSortConfig, setInternalSortConfig] = useState({ key: null, direction: 'desc' });
  // v2.5: Support up to 2 modals for comparison
  const [openModals, setOpenModals] = useState([]); // Array of {song, id} objects
  const [searchTerm, setSearchTerm] = useState('');
  
  // Audience Impact: auto-fetch if not provided externally
  const [autoImpact, setAutoImpact] = useState({});
  useEffect(() => {
    if (externalImpact || !stationId) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    const days = period === 'today' ? 1 : period === 'day' || period === 'yesterday' ? 2 : period === 'month' ? 30 : 7;
    fetch(`${getBackendUrl()}/api/audience-impact/station/${stationId}?days=${days}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.songs) {
          // Station endpoint returns {songs: {normalized_key: {...}}}
          setAutoImpact(d.songs);
        }
      })
      .catch(() => {});
  }, [stationId, period, externalImpact]);
  const impactLookup = externalImpact || autoImpact;
  
  // Handle opening a modal (max 2)
  const handleOpenModal = useCallback((song) => {
    setOpenModals(prev => {
      // Check if this song is already open
      const existingIndex = prev.findIndex(m => 
        m.song.artist === song.artist && m.song.title === song.title
      );
      if (existingIndex >= 0) return prev; // Already open
      
      // If we have 2 modals, replace the oldest one
      if (prev.length >= 2) {
        return [...prev.slice(1), { song, id: Date.now() }];
      }
      return [...prev, { song, id: Date.now() }];
    });
  }, []);
  
  // Handle closing a specific modal
  const handleCloseModal = useCallback((modalId) => {
    setOpenModals(prev => prev.filter(m => m.id !== modalId));
  }, []);
  
  // Use external sortConfig if provided, otherwise use internal
  const sortConfig = externalSortConfig || internalSortConfig;
  
  const isIncomplete = stats?.is_incomplete_period;
  
  // Data mode: "live" = real listeners, "stream" = signal-based analytics
  // Mode is IMMUTABLE - defined at station profile level, NEVER changes by view/tab/filter
  const dataMode = stats?.data_mode || 'stream';
  
  // STRICT MODE SEPARATION (per spec):
  // Live Audience: real TLH, real retention, Avg Listeners shown EVERYWHERE (Total + Dayparts)
  // Stream Analytics: proxy TLH (Audience Activity Index), NO Avg Listeners ANYWHERE
  
  // 1. isLiveAudience: controls Avg Listeners column visibility
  //    - Live Audience: SHOW in Total view AND Dayparts view
  //    - Stream Analytics: NEVER show
  const isLiveAudience = dataMode === 'live';
  
  // 2. useLiveMetrics: controls which TLH to show (real vs proxy)
  //    - Determined by data_mode ONLY
  //    - Dayparts is TIME FILTER ONLY - doesn't change metric source
  const useLiveMetrics = dataMode === 'live';
  
  // 3. isStreamAnalytics: for UI relabeling (TLH → Audience Activity Index)
  const isStreamAnalytics = dataMode === 'stream';

  // 4. shouldHideListenerMetrics: in Live Audience mode, hide retention/early-drop/LSI
  //    columns when the song has zero listener data (peak=0 AND no real samples).
  //    This avoids surfacing fabricated completion-based estimates as if they were
  //    real audience metrics — they are only shown in Stream Analytics where the
  //    user knows the whole table is signal-based.
  const shouldHideListenerMetrics = (song) => {
    if (!isLiveAudience) return false;
    const peak = Number(song?.peak_listeners) || 0;
    const avg = Number(song?.avg_listeners) || 0;
    const reliability = song?.metrics_reliability;
    // Hide only when we truly have no listener data for THIS song
    return peak === 0 && avg === 0 && (reliability === 'none' || reliability === 'estimated');
  };
  
  // Get dynamic thresholds or use defaults
  const thresholds = listenerThresholds?.enabled ? {
    low: listenerThresholds.low || 10,
    medium: listenerThresholds.medium || 30,
    high: listenerThresholds.high || 80
  } : {
    low: 10,
    medium: 30,
    high: 80
  };
  
  // Create reliability label based on thresholds
  const getReliabilityLabel = (reliability) => {
    if (reliability === 'none') return `Λιγότεροι από ${thresholds.low} ακροατές - Ανεπαρκή δεδομένα`;
    if (reliability === 'low') return `${thresholds.low}-${thresholds.medium} ακροατές - Οι τιμές (~) είναι εκτιμήσεις`;
    if (reliability === 'medium') return `${thresholds.medium}-${thresholds.high} ακροατές - Αποδεκτή ακρίβεια`;
    return null;
  };
  
  // Handle column header click for sorting
  const handleSort = useCallback((key) => {
    const newConfig = (() => {
      if (sortConfig.key === key) {
        if (sortConfig.direction === 'desc') {
          return { key, direction: 'asc' };
        } else {
          return { key: null, direction: 'desc' };
        }
      }
      return { key, direction: 'desc' };
    })();
    
    // Update external state if callback provided
    if (onSortChange) {
      onSortChange(newConfig);
    } else {
      setInternalSortConfig(newConfig);
    }
  }, [sortConfig, onSortChange]);
  
  // Sort songs based on current config
  const sortedSongs = useMemo(() => {
    if (!stats?.songs) return [];
    
    const songs = [...stats.songs];
    
    if (!sortConfig.key) {
      return songs;
    }
    
    return songs.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      // Special handling for ranking_category - sort by rank order
      if (sortConfig.key === 'ranking_category') {
        aVal = RANK_ORDER[aVal] || 99;
        bVal = RANK_ORDER[bVal] || 99;
        
        if (sortConfig.direction === 'asc') {
          return aVal - bVal;
        }
        return bVal - aVal;
      }
      
      if (sortConfig.key === 'artist' || sortConfig.key === 'title') {
        aVal = (aVal || '').toLowerCase();
        bVal = (bVal || '').toLowerCase();
        
        if (sortConfig.direction === 'asc') {
          return aVal.localeCompare(bVal, 'el');
        }
        return bVal.localeCompare(aVal, 'el');
      }
      
      aVal = parseFloat(aVal) || 0;
      bVal = parseFloat(bVal) || 0;
      
      if (sortConfig.direction === 'asc') {
        return aVal - bVal;
      }
      return bVal - aVal;
    });
  }, [stats, sortConfig]);
  
  // Filter songs by search term
  const filteredSongs = useMemo(() => {
    if (!searchTerm.trim()) return sortedSongs;
    
    const search = searchTerm.toLowerCase();
    return sortedSongs.filter(song => 
      (song.artist || '').toLowerCase().includes(search) ||
      (song.title || '').toLowerCase().includes(search)
    );
  }, [sortedSongs, searchTerm]);
  
  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <RefreshCw className="w-5 h-5 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!stats) {
    return <p className="text-sm text-gray-500 text-center py-4">Φόρτωση...</p>;
  }

  const hasNoData = stats.total_plays === 0 && (!stats.songs || stats.songs.length === 0);

  if (hasNoData) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-gray-500 dark:text-gray-400">
        <AlertCircle className="w-12 h-12 mb-3 opacity-50" />
        <p className="text-lg font-medium">Δεν υπάρχουν δεδομένα</p>
        <p className="text-sm mt-1">Δεν βρέθηκαν καταγραφές για την επιλεγμένη περίοδο.</p>
        <p className="text-xs mt-2 text-gray-400">Επιλέξτε διαφορετική ημερομηνία ή ελέγξτε αν ο σταθμός ήταν ενεργός.</p>
      </div>
    );
  }

  // Sortable header render function (not a component)
  const renderSortableHeader = (columnKey, label, className = "text-center", title = "") => (
    <th 
      className={`py-1 px-2 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 select-none transition-colors ${className}`}
      onClick={() => handleSort(columnKey)}
      title={title || `Κλικ για ταξινόμηση κατά ${label}`}
    >
      <div className="flex items-center justify-center gap-1">
        {label}
        <SortIndicator 
          sortKey={columnKey} 
          currentKey={sortConfig.key} 
          direction={sortConfig.direction} 
        />
      </div>
    </th>
  );

  return (
    <div className="space-y-3">
      {/* Search Box */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="🔍 Αναζήτηση τραγουδιού ή καλλιτέχνη..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 text-sm"
            data-testid="song-search-input"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {searchTerm && (
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {filteredSongs.length} από {sortedSongs.length} τραγούδια
          </span>
        )}
      </div>
      
      {/* Summary */}
      <div className="flex flex-wrap gap-4 text-sm text-gray-900 dark:text-white">
        <div>
          <span className="text-gray-500 dark:text-gray-400">Plays:</span>{' '}
          <span className="font-medium">{stats.total_plays}</span>
        </div>
        <div>
          <span className="text-gray-500 dark:text-gray-400">Τραγούδια:</span>{' '}
          <span className="font-medium">{stats.unique_songs}</span>
        </div>
        <div>
          <span className="text-gray-500 dark:text-gray-400">
            {stats.show_proxy_tlh ? 'Activity' : 'TLH'}:
          </span>{' '}
          <span className="font-medium text-purple-600 dark:text-purple-400">
            {stats.show_proxy_tlh ? (stats.total_proxy_tlh ?? 0) : (stats.total_tlh ?? 0)}
          </span>
        </div>
        {stats.overall_score !== undefined && stats.overall_score !== null && (
          <div>
            <span className="text-gray-500 dark:text-gray-400">Score:</span>{' '}
            <span className="font-medium text-blue-600 dark:text-blue-400">{stats.overall_score}</span>
          </div>
        )}
        {stats.overall_retention !== undefined && (
          <div>
            <span className="text-gray-500 dark:text-gray-400">Retention:</span>{' '}
            <span className="font-medium text-green-600 dark:text-green-400">{Math.min(stats.overall_retention, 100).toFixed(1)}%</span>
          </div>
        )}
      </div>
      
      {/* Warning for incomplete period (today) */}
      {stats.is_incomplete_period && (
        <div className="mb-3 p-2 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded text-xs text-purple-700 dark:text-purple-300">
          <span className="font-medium">⏳ Preview Mode:</span> Η ημέρα δεν έχει ολοκληρωθεί - δεν εφαρμόζεται ranking. 
          Επιλέξτε <strong>&quot;Χθες&quot;</strong> για πλήρη ανάλυση με κατηγορίες.
        </div>
      )}
      
      {/* Stream Analytics Legend - ONLY for Stream Analytics mode */}
      {isStreamAnalytics && (
        <div className="mb-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-blue-700 dark:text-blue-300">
              <span className="font-semibold">Audience Activity Index:</span>{' '}
              Σχετική ένδειξη δραστηριότητας ακρόασης βασισμένη σε σήμα και σταθερότητα stream.
            </div>
          </div>
        </div>
      )}
      
      {/* Sorting hint + New metrics legend */}
      <div className="flex flex-wrap items-center gap-4 text-xs text-gray-400 dark:text-gray-500">
        <span>💡 Κλικ στους τίτλους για ταξινόμηση</span>
        <span className="border-l border-gray-300 dark:border-gray-600 pl-4">
          <span className="text-gray-500 dark:text-gray-400">Early Drop:</span>{' '}
          <span className="inline-flex gap-0.5 mx-1">
            <span className="w-1.5 h-3 rounded-sm bg-green-500"></span>
            <span className="w-1.5 h-3 rounded-sm bg-green-500"></span>
            <span className="w-1.5 h-3 rounded-sm bg-green-500"></span>
            <span className="w-1.5 h-3 rounded-sm bg-green-500"></span>
          </span>
          Καλό
          <span className="inline-flex gap-0.5 mx-1 ml-2">
            <span className="w-1.5 h-3 rounded-sm bg-yellow-500"></span>
            <span className="w-1.5 h-3 rounded-sm bg-yellow-500"></span>
            <span className="w-1.5 h-3 rounded-sm bg-gray-300 dark:bg-gray-600"></span>
            <span className="w-1.5 h-3 rounded-sm bg-gray-300 dark:bg-gray-600"></span>
          </span>
          Μέτριο/Χαμ.Ret
          <span className="inline-flex gap-0.5 mx-1 ml-2">
            <span className="w-1.5 h-3 rounded-sm bg-red-500"></span>
            <span className="w-1.5 h-3 rounded-sm bg-gray-300 dark:bg-gray-600"></span>
            <span className="w-1.5 h-3 rounded-sm bg-gray-300 dark:bg-gray-600"></span>
            <span className="w-1.5 h-3 rounded-sm bg-gray-300 dark:bg-gray-600"></span>
          </span>
          Κακό
        </span>
        <span className="border-l border-gray-300 dark:border-gray-600 pl-4">
          <span className="text-gray-500 dark:text-gray-400">LSI:</span>{' '}
          💎 Εξαιρετικό · ✨ Καλό · • Μέτριο · ⚡ Ασταθές
        </span>
      </div>
      
      {/* Songs table */}
      {filteredSongs.length > 0 ? (
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          <table className="w-full text-xs" data-testid="song-table">
            <thead className="sticky top-0 bg-gray-100 dark:bg-gray-800 z-10">
              <tr className="text-left text-gray-600 dark:text-gray-400">
                {/* v2.9.87 — Cover art / preview play button column.
                    Sits before # so the visual hierarchy is:
                    cover → rank → metadata. Always rendered (even
                    when songs lack data), so the operator sees the
                    feature is live and just waiting on backfill. */}
                <th className="py-1 px-2 w-12" title="Cover art (Shazam) / 30s preview"></th>
                <th className="py-1 px-2">#</th>
                {renderSortableHeader("artist", "Καλλιτέχνης", "text-left")}
                {renderSortableHeader("title", "Τίτλος", "text-left")}
                {renderSortableHeader("plays", "Plays", "text-center", "Συνολικές αναπαραγωγές σε όλους τους σταθμούς")}
                {/* Hide Avg Listeners header in Stream Analytics mode */}
                {isLiveAudience && renderSortableHeader(
                  "avg_listeners", 
                  "Avg Ακρ.", 
                  "text-center",
                  "Μέσος όρος ταυτόχρονων ακροατών ανά αναπαραγωγή, σταθμισμένος σε όλους τους σταθμούς"
                )}
                {/* Show appropriate TLH header based on mode - NOT affected by isDaypartView */}
                {/* Stream Analytics: "Audience Activity Index" with tooltip */}
                {/* Live Audience: "TLH" */}
                {isStreamAnalytics ? (
                  <th 
                    className="py-1 px-2 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 select-none transition-colors text-center"
                    onClick={() => handleSort("total_proxy_tlh")}
                    title="Δείκτης Δραστηριότητας Ακροατηρίου — εκτιμώμενη βαρύτητα ακρόασης βάσει κάλυψης σταθμών και συχνότητας αναπαραγωγής"
                  >
                    <div className="flex items-center justify-center gap-1">
                      <span className="whitespace-nowrap">Activity</span>
                      <SortIndicator 
                        sortKey="total_proxy_tlh" 
                        currentKey={sortConfig.key} 
                        direction={sortConfig.direction} 
                      />
                    </div>
                  </th>
                ) : (
                  renderSortableHeader(
                    "total_tlh", 
                    "TLH",
                    "text-center",
                    "Σύνολο Ωρών Ακρόασης — υπολογίζεται ανά play (ακροατές × διάρκεια) και αθροίζεται. Plays με πολλούς ακροατές συνεισφέρουν περισσότερο"
                  )
                )}
                {renderSortableHeader("retention_rate", "Ret%", "text-center", "Διατήρηση Ακροατηρίου — ποσοστό ακροατών που παρέμειναν μέχρι τέλος. 100% = κανείς δεν έφυγε")}
                {renderSortableHeader("early_drop_rate", "Early", "text-center", "Πρόωρη Αποχώρηση — ποσοστό ακροατών που έφυγαν στα πρώτα 60\" (intro). Χαμηλό = καλό")}
                {renderSortableHeader("lsi", "LSI", "text-center", "Δείκτης Σταθερότητας Ακροατηρίου — 1.00 = απόλυτα σταθερό κοινό, <0.80 = πολλές εναλλαγές")}
                {renderSortableHeader("composite_score", "Score", "text-center", "Συνολικό Σκορ = log(plays) × 55% + retention × 30% + (100 - early_drop) × 15%")}
                <th className="py-1 px-2 text-center" title="Audience Impact: plays × influence × daypart">
                  <span className="whitespace-nowrap">A.Impact</span>
                </th>
                {renderSortableHeader("release_year", "Έτος", "text-center", "Έτος κυκλοφορίας")}
                {!isIncomplete && (
                  renderSortableHeader("ranking_category", "Rank", "text-center", "Κλικ για ταξινόμηση POWER→CUT")
                )}
              </tr>
            </thead>
            <tbody className="text-gray-900 dark:text-gray-200">
              {filteredSongs.map((song, idx) => (
                <tr 
                  key={`${song.artist}-${song.title}-${idx}`} 
                  className="border-t border-gray-200 dark:border-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700/20"
                  data-testid={`song-row-${idx}`}
                >
                  {/* v2.9.87 — Cover art thumb (Shazam) with hover play
                      overlay (iTunes/Deezer 30s preview). Always
                      rendered; the component degrades to a placeholder
                      music-note when both fields are missing. */}
                  <td className="py-1 px-2 align-middle">
                    <PreviewPlayButton
                      src={song.cover_art_url}
                      previewUrl={song.preview_url}
                      trackKey={`row-${song.normalized_key || `${song.artist}|${song.title}` || idx}`}
                      artist={song.artist}
                      title={song.title}
                      size="sm"
                      testId={`song-row-cover-${idx}`}
                    />
                  </td>
                  <td className="py-1 px-2 text-gray-500">{song.rank || idx + 1}</td>
                  <td 
                    className="py-1 px-2 truncate max-w-[150px] font-medium cursor-pointer hover:text-blue-600 hover:underline" 
                    title="Κλικ για λεπτομέρειες (μέχρι 2 για σύγκριση)"
                    onClick={() => handleOpenModal(song)}
                    data-testid={`song-artist-${idx}`}
                  >
                    {decodeHtmlEntities(song.artist)}
                  </td>
                  <td 
                    className="py-1 px-2 truncate max-w-[150px] text-gray-500 dark:text-gray-400 cursor-pointer hover:text-blue-600 hover:underline" 
                    title="Κλικ για λεπτομέρειες (μέχρι 2 για σύγκριση)"
                    onClick={() => handleOpenModal(song)}
                    data-testid={`song-title-${idx}`}
                  >
                    <span className="flex items-center gap-1">
                      <span className="truncate">{decodeHtmlEntities(song.title)}</span>
                      <ConfidenceBadge confidence={song.chart_confidence} />
                    </span>
                  </td>
                  <td className="py-1 px-2 text-center font-medium">{song.plays}</td>
                  {/* v2.8: Hide Avg Listeners column in Stream Analytics mode */}
                  {isLiveAudience && (
                    <td className="py-1 px-2 text-center text-gray-500 dark:text-gray-400">
                      {/* Show value if avg_listeners exists and > 0 */}
                      {song.avg_listeners !== null && song.avg_listeners > 0 
                        ? (
                          <span title={`Μ.Ο. ταυτόχρονων ακροατών ανά play\nΑκροατικά Αποτυπώματα: ${Math.round(song.avg_listeners * song.plays)} (${song.plays} plays × ${Math.round(song.avg_listeners)} avg)${song.listener_coverage_pct ? `\nΚάλυψη δεδομένων: ${song.listener_coverage_pct}%` : ''}`}>
                            {Math.round(song.avg_listeners)}
                          </span>
                        )
                        : (
                          <span className="text-gray-400" title="Δεν υπάρχουν δεδομένα ακροατών">—</span>
                        )
                      }
                    </td>
                  )}
                  <td className="py-1 px-2 text-center text-purple-600 dark:text-purple-400">
                    {/* TLH/Activity: Use real for Live Audience mode, proxy for Stream Analytics */}
                    {/* CRITICAL: This is determined by data_mode, NOT by isDaypartView */}
                    {useLiveMetrics 
                      ? (
                        <span title={`Σύνολο ωρών ακρόασης\nΥπολογίζεται: ακροατές × διάρκεια ανά play, αθροισμένο\n${song.plays} plays, Avg ${Math.round(song.avg_listeners || 0)} ακροατές`}>
                          {song.total_tlh != null ? song.total_tlh : 0}
                        </span>
                      )
                      : (
                        <span title="Δείκτης Δραστηριότητας — εκτιμώμενη βαρύτητα ακρόασης βάσει κάλυψης σταθμών">
                          {song.total_proxy_tlh ?? song.total_tlh ?? 0}
                        </span>
                      )
                    }
                  </td>
                  <td className="py-1 px-2 text-center">
                    <span 
                      className={
                        song.retention_rate >= 90 ? 'text-green-600' : 
                        song.retention_rate >= 75 ? 'text-yellow-600' : 
                        song.retention_rate >= 60 ? 'text-orange-600' :
                        'text-red-600'
                      }
                      title={song.retention_rate != null && song.retention_rate > 0 
                        ? (song.curve_retention 
                            ? `Διατήρηση Ακροατηρίου: ${Math.min(song.curve_retention, 100)}%\nΒασισμένο σε πραγματικά δείγματα ακροατών κατά τη διάρκεια αναπαραγωγής`
                            : `Εκτιμώμενη Διατήρηση: ~${Math.min(song.retention_rate, 100)}%\nΕκτίμηση βάσει χρόνου αναπαραγωγής (δεν υπάρχουν δείγματα ακροατών)`)
                        : 'Δεν υπάρχουν δεδομένα διατήρησης'}
                    >
                      {shouldHideListenerMetrics(song)
                        ? <span className="text-white/20" title="Δεν υπάρχουν listener samples — Live Audience mode κρύβει τις εκτιμήσεις">—</span>
                        : song.retention_rate != null && song.retention_rate > 0 
                          ? <>
                              {!song.curve_retention && <span className="text-white/30 mr-0.5">~</span>}
                              {Math.min(song.retention_rate, 100).toFixed(0)}%
                              <CurveRetentionBadge 
                                curveRetention={song.curve_retention ? Math.min(song.curve_retention, 100) : null} 
                                legacyRetention={song.retention_rate ? Math.min(song.retention_rate, 100) : null}
                              />
                            </>
                          : <span className="text-white/20">—</span>}
                    </span>
                  </td>
                  {/* Early Drop Indicator - NEW */}
                  <td className="py-1 px-2 text-center">
                    {shouldHideListenerMetrics(song)
                      ? <span className="text-white/20" title="Δεν υπάρχουν listener samples — Live Audience mode κρύβει τις εκτιμήσεις">—</span>
                      : <EarlyDropIndicator 
                          rate={song.early_drop_rate} 
                          retentionRate={song.retention_rate} 
                          hasAdvancedMetrics={song.has_advanced_metrics}
                          metricsReliability={song.metrics_reliability}
                        />}
                  </td>
                  {/* LSI Badge - NEW */}
                  <td className="py-1 px-2 text-center">
                    {shouldHideListenerMetrics(song)
                      ? <span className="text-white/20" title="Δεν υπάρχουν listener samples — Live Audience mode κρύβει τις εκτιμήσεις">—</span>
                      : <LSIBadge 
                          lsi={song.lsi} 
                          hasAdvancedMetrics={song.has_advanced_metrics}
                          metricsReliability={song.metrics_reliability}
                        />}
                  </td>
                  <td className="py-1 px-2 text-center font-medium text-blue-600 dark:text-blue-400" 
                    title={shouldHideListenerMetrics(song)
                      ? 'Δεν υπάρχουν listener samples — Score κρυμμένο σε Live Audience mode'
                      : song.metrics_reliability === 'estimated' 
                        ? 'Εκτιμώμενο σκορ: ανεπαρκή δείγματα ακροατών για ακριβή υπολογισμό' 
                        : `Σκορ = log(plays) × 55% + retention × 30% + (100 - early_drop) × 15%`}>
                    {shouldHideListenerMetrics(song)
                      ? <span className="text-white/20">—</span>
                      : <>
                          {song.metrics_reliability === 'estimated' && <span className="text-blue-400/40 mr-0.5">~</span>}
                          {song.composite_score ?? 0}
                        </>}
                  </td>
                  <td className="py-1 px-2 text-center">
                    {(() => {
                      const imp = impactLookup[song.normalized_key];
                      return imp ? (
                        <AudienceImpactBadge value={imp.audience_impact} level={imp.level} compact />
                      ) : <span className="text-gray-400">—</span>;
                    })()}
                  </td>
                  <td className="py-1 px-2 text-center text-gray-500">
                    {song.release_year || '—'}
                  </td>
                  {!isIncomplete && (
                    <td className="py-1 px-2 text-center font-bold">
                      <RankingBadgeWithTooltip 
                        category={song.ranking_category || song.dominant_category}
                        releaseYearUnknown={song.release_year_unknown}
                        isNewEntry={song.is_new_entry}
                        daysInRotation={song.days_in_rotation}
                        releaseYear={song.release_year}
                        isAddedEntry={song.is_added_entry}
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-gray-500 text-center py-4">Δεν υπάρχουν δεδομένα</p>
      )}
      
      {/* Song Details Modals - rendered via portal, supports up to 2 for comparison */}
      {openModals.map((modal, idx) => createPortal(
        <SongDetailsModal 
          key={modal.id}
          song={modal.song} 
          onClose={() => handleCloseModal(modal.id)}
          stationId={stationId}
          period={period}
          isAdmin={isAdmin}
          userId={userId}
          instanceId={idx}
          totalInstances={openModals.length}
        />,
        document.body
      ))}
    </div>
  );
};

export default SongTable;
